import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import { get } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import moment from 'moment-timezone';

var _class, _descriptor;

// Title a detail page published for its record (setRecordTitle). One tracked cell per record, created on READ too,
// so a tab label rendered before the page computed its title still revalidates when the title arrives.
let TitleCell = (_class = class TitleCell {
  constructor() {
    _initializerDefineProperty(this, "value", _descriptor, this);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "value", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return '';
  }
}), _class);
const TITLES = new WeakMap();
function cellFor(item) {
  let cell = TITLES.get(item);
  if (!cell) {
    cell = new TitleCell();
    TITLES.set(item, cell);
  }
  return cell;
}

/**
 * The title a detail page published for this record, or '' (callers fall back to their own label).
 * @param {object} item record
 * @returns {string}
 */
function recordTitle(item) {
  return item && typeof item === 'object' ? cellFor(item).value : '';
}

/**
 * Publish the page title of a record: the open-record tab (bb-tab-label-nuova) and the browser title
 * (socket-tracker) show it instead of the model label. The browser title, already written at route entry with the
 * old text, gets its first part swapped when it still starts with it.
 * @param {object} item record
 * @param {string} title page title ('' clears)
 * @param {string} [fallback] the text the browser title was written with (the cleaned model label)
 */
function setRecordTitle(item, title, fallback = '') {
  if (!item || typeof item !== 'object') return;
  const cell = cellFor(item);
  const prev = cell.value || fallback;
  const next = String(title ?? '').trim();
  if (cell.value === next) return;
  cell.value = next;
  if (typeof document !== 'undefined' && prev && next && document.title.startsWith(`${prev} | `)) {
    document.title = next + document.title.slice(prev.length);
  }
}

// Fallback words when the organization has no name for the type (tenant names win): translation keys.
const TYPE_NAME = {
  invoice: 'nv.invoice.detail.type_invoice',
  receipt: 'nv.invoice.detail.type_receipt',
  estimate: 'nv.invoice.detail.type_estimate',
  autofattura: 'nv.invoice.detail.type_autofattura'
};
const capitalize = text => {
  const s = String(text ?? '').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

/**
 * Tenant name of the document's type («Fattura», «Ricevuta», «Nota di credito»…), capitalised.
 * @param {object} doc - invoice record or plain row
 * @param {object} intl - intl service
 * @param {object} [organization] - session organization (record or POJO)
 */
function documentTypeName(doc, intl, organization) {
  if (!doc) return '';
  if (get(doc, 'is_creditnote')) return capitalize(intl.t('credit note'));
  const type = get(doc, 'document_type');
  const name = organization ? get(organization, `${type}_name`) : null;
  if (typeof name === 'string' && name.trim()) return capitalize(name);
  return TYPE_NAME[type] ? intl.t(TYPE_NAME[type]) : '';
}

/**
 * «<Tipo> senza numero del <data>» for an invoice record without a number, else null.
 * @param {object} doc - invoice record or plain row
 * @param {object} intl - intl service
 * @param {object} [organization] - session organization
 * @param {string} [name] - the document's name when the caller already has it
 */
function untitledDocumentTitle(doc, intl, organization, name) {
  if (!doc || !intl) return null;
  const type = get(doc, 'document_type');
  if (!type) return null;
  const number = get(doc, 'number');
  if (number !== null && number !== undefined && String(number).trim() !== '') return null;
  const docName = name || documentTypeName(doc, intl, organization);
  if (!docName) return null;
  const raw = get(doc, 'date');
  const date = raw ? moment(raw) : null;
  if (!date?.isValid()) return intl.t('nv.invoice.detail.title_no_number', {
    name: docName
  });
  return intl.t('nv.invoice.detail.title_no_number_date', {
    name: docName,
    date: date.format('DD/MM/YYYY')
  });
}

/**
 * The page title of any fiscal document: untitled sentence, or «<Tipo> n. <numero> del <data>»; null when the record
 * is not a fiscal document (the caller keeps its own label).
 * @param {object} doc - invoice record or plain row
 * @param {object} intl - intl service
 * @param {object} [organization] - session organization
 */
function documentTitle(doc, intl, organization) {
  const untitled = untitledDocumentTitle(doc, intl, organization);
  if (untitled || !doc || !intl || !get(doc, 'document_type')) return untitled;
  const name = documentTypeName(doc, intl, organization);
  if (!name) return null;
  const number = String(get(doc, 'number'));
  const raw = get(doc, 'date');
  const date = raw ? moment(raw) : null;
  return date?.isValid() ? intl.t('nv.invoice.detail.title_number_date', {
    name,
    number,
    date: date.format('DD/MM/YYYY')
  }) : intl.t('nv.invoice.detail.title_number', {
    name,
    number
  });
}

/**
 * A record label without a dangling separator: a booking is labelled «<timestamp> - <client>» and comes back as
 * «17/10/2026 09:52 -» when the second part is empty — the open-record tab and the browser title showed the dash.
 * Nuova only (tab label, socket-tracker title); the classic UI keeps the raw label.
 * @param {*} label the model label
 * @returns {string}
 */
function cleanRecordLabel(label) {
  return String(label ?? '').replace(/[\s\-–·|]+$/u, '').trim();
}

export { cleanRecordLabel, documentTitle, documentTypeName, recordTitle, setRecordTitle, untitledDocumentTitle };
//# sourceMappingURL=nv-document-title.js.map
